import { cn } from '@/lib/utils';
import { WORLDS, type WorldSlug } from '@/lib/worlds';

/**
 * SI-Inspiration in-page world filter for the Story-Ideen section
 * (Sebastian Gate-1 Decision 2B, lock 2026-05-11).
 *
 * 4 chips: "Alle" (default), plus the 3 world names sourced from
 * WORLDS so any future world-pool change propagates automatically.
 * Selection is local to InspirationRoute (useState) — does not
 * affect the navigation TopBar's world tabs (those remain
 * builder-shortcuts per Decision 2 lock).
 */
export type WorldFilterValue = 'all' | WorldSlug;

export interface WorldFilterProps {
  value: WorldFilterValue;
  onChange: (next: WorldFilterValue) => void;
  className?: string;
  testId?: string;
}

const CHIPS: ReadonlyArray<{ value: WorldFilterValue; label: string }> = [
  { value: 'all', label: 'Alle' },
  { value: 'aa', label: WORLDS.AA.name },
  { value: 'sb', label: WORLDS.SB.name },
  { value: 'kp', label: WORLDS.KP.name },
];

export function WorldFilter({ value, onChange, className, testId }: WorldFilterProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Welten-Filter"
      className={cn('flex flex-wrap items-center gap-2', className)}
      data-testid={testId}
    >
      {CHIPS.map((chip) => {
        const isActive = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(chip.value)}
            data-testid={testId ? `${testId}-${chip.value}` : undefined}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-dream-navy/50',
              isActive
                ? 'border-dream-navy bg-dream-navy text-dream-white'
                : 'border-dream-navy/20 bg-dream-white text-dream-navy hover:bg-dream-background',
            )}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}
